/**
 * MÓDULO DE ORÇAMENTOS MAD (MADEIREIRA)
 * 
 * Gerenciamento de orçamentos de venda.
 * Permite selecionar clientes e produtos, calculando automaticamente
 * a quantidade de material necessário com base nas dimensões solicitadas
 * versus as dimensões padrão do produto, alertando sobre fracionamento e sobras.
 */

import React, { useState, useEffect, useMemo } from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography, TableSortLabel,
    TablePagination, CircularProgress, Autocomplete, Grid,
    Alert, Chip, Divider, InputAdornment, useTheme
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    Description as DescriptionIcon,
    Person as PersonIcon,
    ShoppingCart as ShoppingCartIcon,
    Calculate as CalculateIcon,
    Warning as WarningIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import PrintIcon from '@mui/icons-material/Print';
import api from "../../services/api";
import AcessoRestrito from '../../components/AcessoRestrito';
import { verificarAcessoUsuario } from "../../utils/authStandards";
import { tratarErroApi, obterCodHashEmpresa } from "../../utils/crudUtils";
import { formatarMoeda, formatarData } from "../../utils/formatadores";
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";
import { sharedStyles } from "../../styles/sharedStyles";

export default function OrcMad() {
    const theme = useTheme();
    const { addSuccess, addError, addWarning } = useSystemMessagesContext();

    // Estados principais
    const usuarioLogado = JSON.parse(localStorage.getItem("usuario")) || {};
    const [orcamentos, setOrcamentos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [produtos, setProdutos] = useState([]);

    const [loading, setLoading] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [orcamentoSelecionado, setOrcamentoSelecionado] = useState(null);

    // Estados de paginação e filtro
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filtros, setFiltros] = useState({
        numero: '',
        cliente: '',
        vendedor: '',
        data: '',
        total: '',
        status: ''
    });
    const [orderBy, setOrderBy] = useState('id');
    const [order, setOrder] = useState('desc');

    // Estado do Formulário
    const [formData, setFormData] = useState({
        cliente: null,
        st_observacao: '',
        itens: []
    });

    // Estado temporário para adição de item no formulário
    const [itemTemp, setItemTemp] = useState({
        produto: null,
        unidadeSelecionada: null,
        dimensaoPadraoSelecionada: null,
        nu_quantidade: 1,
        nu_comprimento_req: '',
        nu_largura_req: '',
        nu_espessura_req: '',
        vl_preco_venda: 0,
        atributoSelecionado: null,
        nu_qtdAtributo: ''
    });

    const orcamentosFiltrados = useMemo(() => {
        return orcamentos.filter(orc => {
            const numero = (orc.numeroControle || String(orc._id).substr(-6).toUpperCase()).toLowerCase();
            const cliente = (orc.cliente?.nome || '').toLowerCase();
            const vendedor = (orc.vendedorNome || '').toLowerCase();

            let dataStr = '';
            try {
                dataStr = new Date(orc.dataCriacao).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            } catch (e) { }

            const totalStr = formatarMoeda(orc.totais?.totalGeral || 0).toLowerCase();
            const status = (orc.status || '').toLowerCase();

            return numero.includes(filtros.numero.toLowerCase()) &&
                cliente.includes(filtros.cliente.toLowerCase()) &&
                vendedor.includes(filtros.vendedor.toLowerCase()) &&
                dataStr.includes(filtros.data) &&
                totalStr.includes(filtros.total.toLowerCase()) &&
                status.includes(filtros.status.toLowerCase());
        });
    }, [orcamentos, filtros]);

    // Cálculos do Item Temporário
    const infoCalculoItem = useMemo(() => {
        if (!itemTemp.produto) return null;

        const msgs = [];
        let qtdPecasNecessarias = 0;
        let sobra = '';
        let fatorCalculado = 1;
        let qtdSugerida = 0;
        let valorCalculadoUnitario = 0;

        // 1. Identificar Preço Base da Unidade
        // Prioridade: Unidade Selecionada -> Primeira Unidade Disponível
        const unidade = itemTemp.unidadeSelecionada || itemTemp.produto.unidades?.[0];
        const precoBase = parseFloat(unidade?.vl_preco || 0);

        // 2. Identificar Dimensões para Cálculo (m, m², m³)
        // Usa as dimensões REQUISITADAS pelo usuário para o cálculo do preço, 
        // mas valida contra o padrão se necessário.
        const compReq = parseFloat(itemTemp.nu_comprimento_req || 0);
        // Largura/Espessura podem vir do input ou da dimensão padrão se não houver input (mas aqui estamos focando no input)
        // Se o usuário não digita, assume 0, a menos que tenhamos que pegar do padrão para m² se ele só digitou comp?
        // Assumindo inputs explícitos ou populados no onChange do Autocomplete.
        const largReq = parseFloat(itemTemp.nu_largura_req || itemTemp.dimensaoPadraoSelecionada?.nu_largura || 0);
        const espReq = parseFloat(itemTemp.nu_espessura_req || itemTemp.dimensaoPadraoSelecionada?.nu_espessura || 0);

        let metragem = 0;

        // Dimensão Padrão (para saber o TIPO de cálculo que o produto suporta)
        const dimPadrao = itemTemp.produto.dimensoes?.[0];

        if (dimPadrao) {
            // A) Metro Linear (m)
            if (dimPadrao.nu_comprimento && !dimPadrao.nu_largura && !dimPadrao.nu_espessura) {
                if (compReq > 0) {
                    metragem = compReq;
                    valorCalculadoUnitario = metragem * precoBase;
                }
            }
            // B) Metro Quadrado (m²)
            else if (dimPadrao.nu_comprimento && dimPadrao.nu_largura && !dimPadrao.nu_espessura) {
                if (compReq > 0 && largReq > 0) {
                    metragem = compReq * largReq;
                    valorCalculadoUnitario = metragem * precoBase;
                }
            }
            // C) Metro Cúbico (m³)
            else if (dimPadrao.nu_comprimento && dimPadrao.nu_largura && dimPadrao.nu_espessura) {
                if (compReq > 0 && largReq > 0 && espReq > 0) {
                    metragem = compReq * largReq * espReq;
                    valorCalculadoUnitario = metragem * precoBase;
                }
            } else {
                // Caso fallback (UN ou outro): Preço Fixo
                valorCalculadoUnitario = precoBase;
            }
        } else {
            // Sem dimensões cadastradas -> Preço Unitário Simples (UN)
            valorCalculadoUnitario = precoBase;
        }

        // Lógica de Estoque / Sobra (Melhorada)
        let calculoRealizado = false;

        if (compReq > 0) {
            const dimensoesDisponiveis = itemTemp.produto.dimensoes || [];
            const EPSILON = 0.001; // 1mm de tolerância

            // Tenta encontrar a melhor dimensão padrão (a menor que atenda o requisito ou a maior de todas)
            let dimPadraoEfetiva = null;
            let menorSobraComp = Infinity;

            dimensoesDisponiveis.forEach(dim => {
                const cp = parseFloat(dim.nu_comprimento || 0);
                if (cp <= 0) return;

                if (compReq <= cp + EPSILON) {
                    const s = cp - compReq;
                    if (s < menorSobraComp) {
                        menorSobraComp = s;
                        dimPadraoEfetiva = dim;
                    }
                }
            });

            // Se nenhum coube (compReq > todos os padrões), usa o maior para cálculo do fator
            if (!dimPadraoEfetiva && dimensoesDisponiveis.length > 0) {
                dimPadraoEfetiva = dimensoesDisponiveis.reduce((prev, current) => 
                    (parseFloat(prev.nu_comprimento || 0) > parseFloat(current.nu_comprimento || 0)) ? prev : current
                );
            }

            if (dimPadraoEfetiva) {
                const compPadrao = parseFloat(dimPadraoEfetiva.nu_comprimento || 0);
                const largPadrao = parseFloat(dimPadraoEfetiva.nu_largura || 0);
                const espPadrao = parseFloat(dimPadraoEfetiva.nu_espessura || 0);
                const qtdSolicitadaOriginal = parseFloat(itemTemp.nu_quantidade) || 1;

                if (compPadrao > 0) {
                    calculoRealizado = true;
                    if (compReq > compPadrao + EPSILON) {
                        const fator = Math.ceil(compReq / compPadrao);
                        fatorCalculado = fator;
                        qtdSugerida = fator * qtdSolicitadaOriginal;
                        qtdPecasNecessarias = qtdSugerida;
                        const totalMaterial = fator * compPadrao;
                        const sobraCalc = totalMaterial - compReq;

                        msgs.push({
                            tipo: 'warning',
                            texto: `A medida solicitada (${compReq}m) é maior que o padrão (${compPadrao}m). Cada unidade final consumirá ${fator} peças do estoque.`
                        });
                        sobra = sobraCalc > EPSILON ? `${sobraCalc.toFixed(2)}m` : '';
                    } else {
                        qtdPecasNecessarias = 1 * qtdSolicitadaOriginal;
                        qtdSugerida = qtdPecasNecessarias;
                        
                        const difComp = Math.abs(compPadrao - compReq);
                        const difLarg = (largPadrao > 0 && largReq > 0) ? Math.abs(largPadrao - largReq) : 0;
                        const difEsp = (espPadrao > 0 && espReq > 0) ? Math.abs(espPadrao - espReq) : 0;

                        if (difComp > EPSILON) {
                            sobra = `${(compPadrao - compReq).toFixed(2)}m`;
                            msgs.push({
                                tipo: 'info',
                                texto: `Corte normal.`
                            });
                        } else {
                            sobra = '';
                            if (difLarg > EPSILON || difEsp > EPSILON) {
                                msgs.push({
                                    tipo: 'info',
                                    texto: `Corte exato no comprimento. Verifique sobras laterais.`
                                });
                            } else {
                                msgs.push({
                                    tipo: 'success',
                                    texto: `Corte exato. Aproveitamento total da peça.`
                                });
                            }
                        }
                    }
                }
            }
        }

        if (!calculoRealizado) {
            // Sem dimensão de corte ativa ou produto sem dimensões padrão
            const qtdInput = parseFloat(itemTemp.nu_quantidade) || 1;

            // Regra: Se unidade for 'UN' e quantidade fracionada -> Arredondar para cima (Próximo Inteiro)
            const stUnidade = itemTemp.unidadeSelecionada?.st_unidade || itemTemp.produto.unidades?.[0]?.st_unidade;

            if (stUnidade === 'UN' && !Number.isInteger(qtdInput)) {
                const qtdArredondada = Math.ceil(qtdInput);
                qtdPecasNecessarias = qtdArredondada;
                qtdSugerida = qtdArredondada;

                msgs.push({
                    tipo: 'warning',
                    texto: `Item vendido por UN não pode ser fracionado no estoque. Consumo ajustado para ${qtdArredondada} un.`
                });
            } else {
                qtdPecasNecessarias = qtdInput;
                qtdSugerida = qtdInput;
            }
        }

        return {
            msgs,
            qtdPecasNecessarias,
            sobra,
            fatorCalculado,
            qtdSugerida,
            valorCalculadoUnitario
        };

    }, [itemTemp.produto, itemTemp.dimensaoPadraoSelecionada, itemTemp.nu_comprimento_req, itemTemp.nu_largura_req, itemTemp.nu_espessura_req, itemTemp.nu_quantidade, itemTemp.unidadeSelecionada]);

    // Atualiza o preço no formulário quando o cálculo muda
    useEffect(() => {
        if (infoCalculoItem && infoCalculoItem.valorCalculadoUnitario !== undefined) {
            setItemTemp(prev => ({
                ...prev,
                vl_preco: infoCalculoItem.valorCalculadoUnitario.toFixed(2)
            }));
        }
    }, [infoCalculoItem]);

    // Atualiza a quantidade do atributo quando muda atributo, quantidade ou dimensões
    useEffect(() => {
        if (itemTemp.atributoSelecionado) {
            const valorAttr = parseFloat(itemTemp.atributoSelecionado.st_valorAtributo) || 0;
            const qtd = parseFloat(itemTemp.nu_quantidade) || 0;
            const comp = parseFloat(itemTemp.nu_comprimento_req) || 0;
            const larg = parseFloat(itemTemp.nu_largura_req) || 0;
            const esp = parseFloat(itemTemp.nu_espessura_req) || 0;

            let finalQtdAtrib = valorAttr;

            // Se possui todas as dimensões preenchidas (considerando que são em metros para m³)
            if (comp > 0 && larg > 0 && esp > 0) {
                // Cálculo: Valor do Atributo * Volume Total (m³)
                // Volume = Comp(m) * Larg(m) * Esp(m) * Qtd
                finalQtdAtrib = valorAttr * (comp * larg * esp) * qtd;
            }

            setItemTemp(prev => ({
                ...prev,
                nu_qtdAtributo: finalQtdAtrib.toFixed(3)
            }));
        } else {
            setItemTemp(prev => ({
                ...prev,
                nu_qtdAtributo: ''
            }));
        }
    }, [itemTemp.atributoSelecionado, itemTemp.nu_quantidade, itemTemp.nu_comprimento_req, itemTemp.nu_largura_req, itemTemp.nu_espessura_req]);

    const [empresa, setEmpresa] = useState(null);

    const carregarEmpresa = async () => {
        try {
            const codHash = usuarioLogado.st_codHashEmpresa;
            const res = await api.get('/empresas');
            if (Array.isArray(res.data)) {
                const emp = res.data.find(e => e.st_codHashEmpresa === codHash);
                if (emp) setEmpresa(emp);
            }
        } catch (error) {
            console.error("Erro ao carregar dados da empresa:", error);
        }
    };

    const carregarOrcamentos = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/orcamentos?ts=${new Date().getTime()}`);
            setOrcamentos(res.data || []);
        } catch (error) {
            console.error("Erro ao carregar orçamentos:", error);
            addError("Erro ao carregar lista de orçamentos.");
        } finally {
            setLoading(false);
        }
    };

    // Salvar Orçamento (Novo ou Edição)
    const handleSalvar = async () => {
        if (!formData.cliente) {
            addWarning("Selecione um cliente.");
            return;
        }
        if (formData.itens.length === 0) {
            addWarning("Adicione pelo menos um item ao orçamento.");
            return;
        }

        const totalGeral = formData.itens.reduce((acc, item) => {
            const p = parseFloat(item.vl_preco) || 0;
            const q = parseFloat(item.nu_quantidade) || 0;
            const d = parseFloat(item.nu_desconto) || 0;
            if (item.isKitComponent) return acc;
            return acc + ((p * q) * (1 - (d / 100)));
        }, 0);

        // Prepara objeto para o backend (Snapshot de dados)
        const payload = {
            cliente: {
                id: formData.cliente.id,
                nome: formData.cliente.nome_completo || formData.cliente.razao_social,
                documento: formData.cliente.cpf || formData.cliente.cnpj,
                endereco: `${formData.cliente.logradouro || ''}, ${formData.cliente.numero || ''} - ${formData.cliente.bairro || ''}`,
                telefone: formData.cliente.celular || formData.cliente.telefone,
                email: formData.cliente.email
            },
            itens: formData.itens.map(item => ({
                produto: {
                    id: item.produto.produtoId || item.produto.id,
                    nome: item.produto.st_nomeProduto || item.produtoNome
                },
                dimensoes: {
                    comprimento: parseFloat(item.nu_comprimento_req) || 0,
                    largura: parseFloat(item.nu_largura_req) || 0,
                    espessura: parseFloat(item.nu_espessura_req) || 0
                },
                unidade: item.unidadeSelecionada?.st_unidade || item.st_unidade || 'UN',
                quantidade: parseFloat(item.nu_quantidade) || 0,
                precoUnitario: parseFloat(item.vl_preco) || 0,
                descontoPercentual: parseFloat(item.nu_desconto) || 0,
                subtotal: (parseFloat(item.vl_preco) * parseFloat(item.nu_quantidade)) * (1 - ((parseFloat(item.nu_desconto) || 0) / 100)),
                isKitHeader: !!item.isKitHeader,
                isKitComponent: !!item.isKitComponent,
                atributo: item.atributoSelecionado ? {
                    id: item.atributoSelecionado.atributoId,
                    nome: item.atributoSelecionado.st_nomeAtributo,
                    valorUnitario: parseFloat(item.atributoSelecionado.st_valorAtributo) || 0
                } : null,
                nu_qtdAtributo: parseFloat(item.nu_qtdAtributo) || 0
            })),
            totais: {
                totalGeral: totalGeral
            },
            observacoes: formData.st_observacao,
            status: 'ABERTO',
            vendedorNome: usuarioLogado.nome || usuarioLogado.st_nome || 'Sistema'
        };

        try {
            if (orcamentoSelecionado && orcamentoSelecionado._id) {
                await api.put(`/orcamentos/${orcamentoSelecionado._id}`, payload);
                addSuccess("Orçamento atualizado com sucesso!");
            } else {
                await api.post('/orcamentos', payload);
                addSuccess("Orçamento salvo com sucesso!");
            }
            setOpenForm(false);
            carregarOrcamentos();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            addError("Erro ao salvar orçamento.");
        }
    };

    const handleExcluirOrcamento = async (id) => {
        if (!window.confirm("Deseja realmente excluir este orçamento?")) return;
        try {
            await api.delete(`/orcamentos/${id}`);
            addSuccess("Orçamento excluído.");
            carregarOrcamentos();
        } catch (error) {
            addError("Erro ao excluir orçamento.");
        }
    };

    // Funções de Carregamento
    const carregarDados = async () => {
        try {
            const [resProd, resKit] = await Promise.all([
                api.get('/prodmad'),
                api.get('/kitmad')
            ]);

            let listaProdutos = [];
            let listaKits = [];

            if (Array.isArray(resProd.data)) {
                listaProdutos = resProd.data.map(item => item.produto || item).filter(p => p && p.produtoId);
            }

            if (Array.isArray(resKit.data)) {
                listaKits = resKit.data.map(item => {
                    const kit = item.kit || item;
                    return { ...kit, isKit: true, st_nomeProduto: `[KIT] ${kit.st_nomeProduto || kit.st_nomeKit}` };
                });
            }

            // Merge: Produtos normais + Kits
            const produtosMap = new Map();
            listaProdutos.forEach(p => produtosMap.set(p.produtoId, p));
            listaKits.forEach(k => produtosMap.set(k.produtoId, k));

            setProdutos(Array.from(produtosMap.values()));

        } catch (error) {
            console.error("Erro ao carregar produtos/kits:", error);
            addError("Erro ao carregar lista de produtos e kits.");
        }
    };

    const carregarClientes = async () => {
        try {
            const codHashEmpresa = obterCodHashEmpresa();
            const res = await api.get(`/clientes?st_codHashEmpresa=${codHashEmpresa}`);
            const dados = Array.isArray(res.data) ? res.data : (res.data.dados || []);
            setClientes(dados);
        } catch (error) {
            console.error("Erro ao carregar clientes", error);
        }
    };

    useEffect(() => {
        carregarDados();
        carregarClientes();
        carregarEmpresa();
        carregarOrcamentos();
    }, []);

    const handleImprimir = async () => {
        if (!formData.itens || formData.itens.length === 0) {
            addWarning("Adicione itens ao orçamento para imprimir.");
            return;
        }

        try {
            // Preparar dados para backend renderizar o HTML
            const totalGeral = formData.itens.reduce((acc, item) => {
                const p = parseFloat(item.vl_preco) || 0;
                const q = parseFloat(item.nu_quantidade) || 0;
                const d = parseFloat(item.nu_desconto) || 0;
                if (item.isKitComponent) return acc;
                return acc + ((p * q) * (1 - (d / 100)));
            }, 0);

            const payload = {
                id: orcamentoSelecionado?.id,
                numero: orcamentoSelecionado ? orcamentoSelecionado.id : 'PROVISÓRIO',
                data: formatarData(new Date()),
                validade: '3 dias',
                observacao: formData.st_observacao,
                vendedorNome: usuarioLogado.nome || usuarioLogado.st_nome || 'Sistema',
                empresa: empresa, // Dados completos da empresa carregados via rota

                clienteNome: formData.cliente?.nome_completo || formData.cliente?.razao_social,
                clienteDoc: formData.cliente?.cpf || formData.cliente?.cnpj,
                clienteEndereco: formData.cliente ? `${formData.cliente.endereco || ''}, ${formData.cliente.numero || ''} - ${formData.cliente.bairro || ''} - ${formData.cliente.cidade || ''}/${formData.cliente.estado || ''}` : '',
                clienteTel: formData.cliente?.telefone_celular || formData.cliente?.telefone_fixo,

                totalGeral: totalGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),

                itens: formData.itens.map(item => ({
                    produtoNome: item.produto.st_nomeProduto,
                    dimensoes: (!item.isKitHeader && item.nu_comprimento_req) ? `${item.nu_comprimento_req}m` : '',
                    unidade: item.unidadeSelecionada?.st_unidade || 'UN',
                    quantidade: item.nu_quantidade,
                    vl_preco: parseFloat(item.vl_preco),
                    vl_precoFormatted: parseFloat(item.vl_preco).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    nu_desconto: parseFloat(item.nu_desconto) || 0,
                    nu_descontoFormatted: (parseFloat(item.nu_desconto) || 0) > 0 ? ((parseFloat(item.nu_desconto) || 0).toFixed(2).replace('.', ',').replace(',00', '') + '%') : '-',
                    subtotal: (parseFloat(item.vl_preco) * parseFloat(item.nu_quantidade)) * (1 - ((parseFloat(item.nu_desconto) || 0) / 100)),
                    subtotalFormatted: ((parseFloat(item.vl_preco) * parseFloat(item.nu_quantidade)) * (1 - ((parseFloat(item.nu_desconto) || 0) / 100))).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    atributoNome: item.atributoSelecionado ? item.atributoSelecionado.st_nomeAtributo : '',
                    nu_qtdAtributo: item.nu_qtdAtributo ? parseFloat(item.nu_qtdAtributo) : null,
                    isKitHeader: item.isKitHeader,
                    isKitComponent: item.isKitComponent
                }))
            };

            const response = await api.post('/prodmad/imprimir', payload);

            // Abrir HTML retornado em nova janela
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(response.data);
                printWindow.document.close();
            } else {
                addWarning("Permita pop-ups para imprimir.");
            }

        } catch (error) {
            console.error("Erro ao imprimir:", error);
            addError("Erro ao gerar impressão do orçamento.");
        }
    };

    const handleAddItem = async () => {
        if (!itemTemp.produto) {
            addWarning("Selecione um produto.");
            return;
        }
        if (!itemTemp.nu_quantidade || itemTemp.nu_quantidade <= 0) {
            addWarning("Informe a quantidade.");
            return;
        }

        // VERIFICAÇÃO SE É UM KIT
        if (itemTemp.produto.isKit || itemTemp.produto.bo_kit === 1) {
            try {
                // Busca detalhes completos do Kit (itens)
                const resKitDetalhe = await api.get(`/kitmad/${itemTemp.produto.produtoId}`);
                const kitCompleto = resKitDetalhe.data;
                const itensDoKit = kitCompleto.itensDoKit || kitCompleto.itens || [];

                if (itensDoKit.length === 0) {
                    addWarning("Este kit não possui itens cadastrados.");
                    return;
                }

                const qtdKit = parseFloat(itemTemp.nu_quantidade) || 1;

                // Calcular Preço do Kit (Soma dos itens ou Preço Personalizado)
                let precoKitUnitario = parseFloat(kitCompleto.precoKit?.vl_precoPersonalizado || 0);

                // Se não tem preço personalizado, soma os componentes
                if (precoKitUnitario <= 0) {
                    precoKitUnitario = 0; // Reset para somar
                    itensDoKit.forEach(comp => {
                        const qtdComp = parseFloat(comp.nu_quantidade || comp.quantidade || 1);
                        const precoComp = parseFloat(comp.produto?.vl_venda || 0);
                        precoKitUnitario += (qtdComp * precoComp);
                    });
                }

                const valorTotalKit = precoKitUnitario * qtdKit;

                // 1. Adicionar Linha do KIT (Cabeçalho do Kit)
                const linhaKit = {
                    produto: {
                        ...itemTemp.produto,
                        st_nomeProduto: itemTemp.produto.st_nomeProduto
                    },
                    nu_quantidade: qtdKit,
                    vl_preco: precoKitUnitario,
                    unidadeSelecionada: { st_unidade: 'KIT' },
                    isKitHeader: true,
                    subtotal: valorTotalKit
                };

                // 2. Adicionar Linhas dos Componentes
                const linhasComponentes = itensDoKit.map(comp => {
                    const qtdNoKit = parseFloat(comp.nu_quantidade || comp.quantidade || 1);
                    return {
                        produto: {
                            ...comp.produto,
                            st_nomeProduto: `↳ ${comp.produto?.st_nomeProduto || 'Item Indefinido'}`
                        },
                        nu_quantidade: qtdKit * qtdNoKit,
                        vl_preco: 0,
                        unidadeSelecionada: comp.produto?.unidades?.[0] || { st_unidade: 'UN' },
                        isKitComponent: true,
                        kitPaiId: itemTemp.produto.produtoId
                    };
                });

                setFormData(prev => ({
                    ...prev,
                    itens: [...prev.itens, linhaKit, ...linhasComponentes]
                }));

                // Reset
                setItemTemp({
                    produto: null,
                    nu_quantidade: 1,
                    nu_comprimento_req: '',
                    nu_largura_req: '',
                    nu_espessura_req: '',
                    dimensaoPadraoSelecionada: null,
                    unidadeSelecionada: null,
                    unidadeSelecionada: null,
                    vl_preco: '',
                    atributoSelecionado: null,
                    nu_qtdAtributo: ''
                });

            } catch (error) {
                console.error("Erro ao adicionar kit:", error);
                addError("Erro ao processar o kit. Verifique os dados.");
            }
            return;
        }

        // PRODUTO NORMAL
        const item = {
            produto: itemTemp.produto,
            nu_quantidade: itemTemp.nu_quantidade,
            nu_comprimento_req: itemTemp.nu_comprimento_req,
            nu_largura_req: itemTemp.nu_largura_req,
            nu_espessura_req: itemTemp.nu_espessura_req,
            dimensaoPadraoSelecionada: itemTemp.dimensaoPadraoSelecionada,
            unidadeSelecionada: itemTemp.unidadeSelecionada,
            calculo: infoCalculoItem,
            vl_preco: itemTemp.vl_preco,
            nu_desconto: itemTemp.nu_desconto,
            atributoSelecionado: itemTemp.atributoSelecionado,
            nu_qtdAtributo: itemTemp.nu_qtdAtributo
        };

        setFormData(prev => ({
            ...prev,
            itens: [...prev.itens, item]
        }));

        setItemTemp({
            produto: null,
            nu_quantidade: 1,
            nu_comprimento_req: '',
            nu_largura_req: '',
            nu_espessura_req: '',
            dimensaoPadraoSelecionada: null,
            unidadeSelecionada: null,
            vl_preco: '',
            nu_desconto: '',
            atributoSelecionado: null,
            nu_qtdAtributo: ''
        });
    };

    const handleRemoverItem = (index) => {
        const itemAlvo = formData.itens[index];

        let novosItens = [...formData.itens];

        if (itemAlvo.isKitHeader) {
            let countToRemove = 1;
            for (let i = index + 1; i < novosItens.length; i++) {
                if (novosItens[i].isKitComponent) {
                    countToRemove++;
                } else {
                    break;
                }
            }
            novosItens.splice(index, countToRemove);
        } else if (itemAlvo.isKitComponent) {
            addWarning("Não é possível remover um componente individual de um kit. Remova o kit inteiro.");
            return;
        } else {
            novosItens.splice(index, 1);
        }

        setFormData(prev => ({ ...prev, itens: novosItens }));
    };

    const abrirNovoOrcamento = () => {
        setOrcamentoSelecionado(null);
        setFormData({
            cliente: null,
            st_observacao: '',
            itens: []
        });
        setOpenForm(true);
    };

    if (!verificarAcessoUsuario("orcmad") && !verificarAcessoUsuario("vendas")) {
        // Fallback
    }

    return (
        <AcessoRestrito modulo="orcmad">
            <Box sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}>

                {/* Box interno com margens de 1,5% */}
                <Box sx={{
                    margin: '0 1.5% 1.5% 1.5%',
                    height: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}>

                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: { xs: 2, sm: 3 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <DescriptionIcon sx={{
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                                color: '#FFC107'
                            }} />
                            Orçamentos de Venda
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={abrirNovoOrcamento}
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: { xs: 'auto', sm: '200px' },
                                backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                                color: theme.palette.mode === 'dark' ? '#000000' : theme.palette.primary.contrastText,
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? '#f5f5f5' : theme.palette.primary.dark
                                }
                            }}
                        >
                            <AddIcon /> Novo Orçamento
                        </Button>
                    </Box>

                    {/* Tabela */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'auto',
                            // Alturas calculadas conforme padrão
                            height: {
                                xs: 'calc(90vh - 140px)',
                                sm: 'calc(90vh - 120px)'
                            },
                            maxHeight: {
                                xs: 'calc(90vh - 140px)',
                                sm: 'calc(90vh - 120px)'
                            },
                            width: '100%',
                            borderRadius: 2,
                            boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                    >
                        <Table stickyHeader sx={sharedStyles.table}>
                            <TableHead sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                backgroundColor: (theme) => theme.palette.background.paper,
                                '& .MuiTableCell-root': {
                                    backgroundColor: 'inherit',
                                    borderBottom: '1px solid rgba(224, 224, 224, 1)'
                                }
                            }}>
                                <TableRow>
                                    <TableCell sx={{ minWidth: '130px', width: '130px', verticalAlign: 'top' }}>
                                        <TableSortLabel
                                            active={orderBy === 'numeroControle'}
                                            direction={orderBy === 'numeroControle' ? order : 'asc'}
                                            onClick={() => { setOrderBy('numeroControle'); setOrder(order === 'asc' ? 'desc' : 'asc'); }}
                                        >
                                            Nº Seq.
                                        </TableSortLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Filtrar..."
                                            value={filtros.numero}
                                            onChange={(e) => setFiltros({ ...filtros, numero: e.target.value })}
                                            sx={{
                                                mt: 1,
                                                '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: '350px', width: 'auto', verticalAlign: 'top' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem', lineHeight: '1.5rem' }}>Cliente</Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Filtrar..."
                                            value={filtros.cliente}
                                            onChange={(e) => setFiltros({ ...filtros, cliente: e.target.value })}
                                            sx={{
                                                mt: 1,
                                                '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: '150px', verticalAlign: 'top' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem', lineHeight: '1.5rem' }}>Vendedor</Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Filtrar..."
                                            value={filtros.vendedor}
                                            onChange={(e) => setFiltros({ ...filtros, vendedor: e.target.value })}
                                            sx={{
                                                mt: 1,
                                                '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: '150px', width: '150px', verticalAlign: 'top' }}>
                                        <TableSortLabel
                                            active={orderBy === 'dataCriacao'}
                                            direction={orderBy === 'dataCriacao' ? order : 'asc'}
                                            onClick={() => { setOrderBy('dataCriacao'); setOrder(order === 'asc' ? 'desc' : 'asc'); }}
                                        >
                                            Data
                                        </TableSortLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Filtrar..."
                                            value={filtros.data}
                                            onChange={(e) => setFiltros({ ...filtros, data: e.target.value })}
                                            sx={{
                                                mt: 1,
                                                '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: { xs: '120px', sm: '120px' }, minWidth: '120px', verticalAlign: 'top' }}>
                                        <TableSortLabel
                                            active={orderBy === 'total'}
                                            direction={orderBy === 'total' ? order : 'asc'}
                                            onClick={() => { setOrderBy('total'); setOrder(order === 'asc' ? 'desc' : 'asc'); }}
                                        >
                                            Total
                                        </TableSortLabel>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Filtrar..."
                                            value={filtros.total}
                                            onChange={(e) => setFiltros({ ...filtros, total: e.target.value })}
                                            sx={{
                                                mt: 1,
                                                '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: '100px', verticalAlign: 'top' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem', lineHeight: '1.5rem' }}>Status</Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Filtrar..."
                                            value={filtros.status}
                                            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                                            sx={{
                                                mt: 1,
                                                '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ verticalAlign: 'middle' }}>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orcamentosFiltrados.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                                                <Typography variant="body1" color="text.secondary">Nenhum orçamento registrado.</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    orcamentosFiltrados.map(orc => (
                                        <TableRow key={orc._id} hover>
                                            <TableCell>{orc.numeroControle || String(orc._id).substr(-6).toUpperCase()}</TableCell>
                                            <TableCell>{orc.cliente?.nome || 'Cliente não identificado'}</TableCell>
                                            <TableCell>{orc.vendedorNome || '-'}</TableCell>
                                            <TableCell>{(() => {
                                                try {
                                                    const d = new Date(orc.dataCriacao);
                                                    if (isNaN(d.getTime())) return '-';
                                                    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                                                } catch (e) { return '-'; }
                                            })()}</TableCell>
                                            <TableCell>{formatarMoeda(orc.totais?.totalGeral || 0)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={orc.status || 'ABERTO'}
                                                    size="small"
                                                    color={orc.status === 'APROVADO' ? 'success' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" onClick={() => {
                                                    setOrcamentoSelecionado(orc);
                                                    setFormData({
                                                        st_observacao: orc.observacoes || '',
                                                        cliente: { ...orc.cliente, id: orc.cliente.id, nome_completo: orc.cliente.nome }, // Adapter simples
                                                        itens: orc.itens.map(it => ({
                                                            ...it,
                                                            produto: { produtoId: it.produto.id, st_nomeProduto: it.produto.nome },
                                                            nu_quantidade: it.quantidade,
                                                            vl_preco: it.precoUnitario,
                                                            nu_desconto: it.descontoPercentual,
                                                            nu_comprimento_req: it.dimensoes?.comprimento,
                                                            nu_largura_req: it.dimensoes?.largura,
                                                            nu_espessura_req: it.dimensoes?.espessura,
                                                            nu_espessura_req: it.dimensoes?.espessura,
                                                            unidadeSelecionada: { st_unidade: it.unidade },
                                                            atributoSelecionado: it.atributo ? {
                                                                st_nomeAtributo: it.atributo.nome,
                                                                st_valorAtributo: it.atributo.valorUnitario,
                                                                atributoId: it.atributo.id,
                                                                st_tipo: it.atributo.st_tipo || 'QUANTIDADE'
                                                            } : null,
                                                            nu_qtdAtributo: it.nu_qtdAtributo
                                                        }))
                                                    });
                                                    setOpenForm(true);
                                                }}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleExcluirOrcamento(orc._id)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {/* Dialog de Cadastro - Layout Box Flexível (Full Width) */}
                <Dialog
                    open={openForm}
                    onClose={() => setOpenForm(false)}
                    maxWidth="xl"
                    fullWidth
                    PaperProps={{
                        sx: {
                            ...sharedStyles.dialogPaper,
                            height: '90vh',
                            maxHeight: '90vh'
                        }
                    }}
                    sx={sharedStyles.dialog}
                >
                    <DialogTitle sx={{
                        ...sharedStyles.dialogTitle,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        pb: 2
                    }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <Box display="flex" alignItems="center">
                                <DescriptionIcon sx={{ mr: 1.5, fontSize: '1.8rem' }} />
                                <Box>
                                    <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
                                        {orcamentoSelecionado ? `Orçamento #${orcamentoSelecionado.id}` : 'Novo Orçamento'}
                                    </Typography>
                                    <Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }}>
                                        Preencha os dados abaixo para gerar a cotação
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                aria-label="close"
                                onClick={() => setOpenForm(false)}
                                sx={{ color: 'inherit' }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ ...sharedStyles.dialogContent, p: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5' }}>

                        {/* SEÇÃO 1: CABEÇALHO DO ORÇAMENTO (BOX FLEX) */}
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            flexWrap: 'wrap',
                            mb: 3,
                            p: 2,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2
                        }}>
                            <Box sx={{ flex: '0 1 200px' }}>
                                <TextField
                                    fullWidth
                                    label="Nº Orçamento"
                                    value={orcamentoSelecionado?.numeroControle || (orcamentoSelecionado?._id ? String(orcamentoSelecionado._id).substr(-6).toUpperCase() : 'PROVISÓRIO')}
                                    disabled
                                    variant="outlined"
                                    sx={{
                                        ...sharedStyles.formField,
                                        '& .MuiInputBase-input': {
                                            fontWeight: 'bold',
                                            letterSpacing: '1px'
                                        }
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: '1 1 400px' }}>
                                <Autocomplete
                                    options={clientes}
                                    getOptionLabel={(option) => option.nome_completo || option.razao_social || 'Sem Nome'}
                                    value={formData.cliente}
                                    onChange={(_, newValue) => setFormData(prev => ({ ...prev, cliente: newValue }))}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Cliente / Destinatário"
                                            required
                                            placeholder="Selecione o cliente..."
                                            sx={sharedStyles.formField}
                                            variant="outlined"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Box>
                            <Box sx={{ flex: '0 1 200px' }}>
                                <TextField
                                    fullWidth
                                    label="Vendedor"
                                    value={orcamentoSelecionado?.vendedorNome || usuarioLogado.nome || usuarioLogado.st_nome || 'Sistema'}
                                    disabled
                                    variant="outlined"
                                    sx={sharedStyles.formField}
                                />
                            </Box>
                            <Box sx={{ flex: '0 1 180px' }}>
                                <TextField
                                    fullWidth
                                    label="Data Emissão"
                                    value={orcamentoSelecionado?.dataCriacao ? new Date(orcamentoSelecionado.dataCriacao).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    disabled
                                    variant="outlined"
                                    sx={sharedStyles.formField}
                                />
                            </Box>
                            <Box sx={{ flex: '0 1 180px' }}>
                                <TextField
                                    fullWidth
                                    label="Status"
                                    value={orcamentoSelecionado?.status || 'ABERTO'}
                                    disabled
                                    variant="outlined"
                                    sx={{
                                        ...sharedStyles.formField,
                                        '& .MuiInputBase-input': {
                                            fontWeight: 'bold',
                                            color: 'warning.main'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* SEÇÃO 2: GESTÃO DE ITENS */}
                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: 0.5 }}>
                                Itens do Orçamento
                            </Typography>
                            <Divider sx={{ flexGrow: 1 }} />
                        </Box>

                        <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                            {/* Barra de Adição (BOX FLEX) */}
                            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: '2 1 200px' }}>
                                        <Autocomplete
                                            options={produtos}
                                            getOptionLabel={(option) => option.st_nomeProduto || ''}
                                            value={itemTemp.produto}
                                            onChange={(_, newValue) => {
                                                const dimensaoPadrao = newValue?.dimensoes?.[0] || null;
                                                const unidadePadrao = newValue?.unidades?.[0] || null;
                                                const precoPadrao = newValue?.vl_venda || '';

                                                setItemTemp(prev => ({
                                                    ...prev,
                                                    produto: newValue,
                                                    dimensaoPadraoSelecionada: dimensaoPadrao,
                                                    unidadeSelecionada: unidadePadrao,
                                                    nu_comprimento_req: dimensaoPadrao?.nu_comprimento || '',
                                                    nu_largura_req: dimensaoPadrao?.nu_largura || '',
                                                    nu_espessura_req: dimensaoPadrao?.nu_espessura || '',
                                                    vl_preco: precoPadrao,
                                                    atributoSelecionado: null,
                                                    nu_qtdAtributo: ''
                                                }));
                                            }}
                                            fullWidth
                                            renderInput={(params) => <TextField {...params} label="Adicionar Produto" placeholder="Busque por nome..." size="small" sx={{ bgcolor: 'background.paper' }} fullWidth />}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '0 1 80px' }}>
                                        <TextField
                                            label="Comp. (m)"
                                            type="number"
                                            size="small"
                                            value={itemTemp.nu_comprimento_req}
                                            onChange={(e) => setItemTemp(prev => ({ ...prev, nu_comprimento_req: e.target.value }))}
                                            disabled={!itemTemp.produto}
                                            fullWidth
                                            sx={{ bgcolor: 'background.paper' }}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '0 1 70px' }}>
                                        <TextField
                                            label="Unid."
                                            size="small"
                                            value={itemTemp.unidadeSelecionada?.st_unidade || 'UN'}
                                            disabled
                                            fullWidth
                                            sx={{ bgcolor: 'background.paper' }}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '0 1 70px' }}>
                                        <TextField
                                            label="Qtd"
                                            type="number"
                                            size="small"
                                            value={itemTemp.nu_quantidade}
                                            onChange={(e) => setItemTemp(prev => ({ ...prev, nu_quantidade: e.target.value }))}
                                            disabled={!itemTemp.produto}
                                            fullWidth
                                            sx={{ bgcolor: 'background.paper' }}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '0 1 100px' }}>
                                        <TextField
                                            label="Preço Unit. (R$)"
                                            size="small"
                                            value={itemTemp.vl_preco || ''}
                                            onChange={(e) => setItemTemp(prev => ({ ...prev, vl_preco: e.target.value }))}
                                            disabled={!itemTemp.produto}
                                            fullWidth
                                            sx={{ bgcolor: 'background.paper' }}
                                            InputProps={{
                                                startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>R$</Typography>
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '0 1 70px' }}>
                                        <TextField
                                            label="Desc. (%)"
                                            type="number"
                                            size="small"
                                            value={itemTemp.nu_desconto || ''}
                                            onChange={(e) => setItemTemp(prev => ({ ...prev, nu_desconto: e.target.value }))}
                                            disabled={!itemTemp.produto}
                                            fullWidth
                                            sx={{ bgcolor: 'background.paper' }}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '1 1 150px' }}>
                                        <Autocomplete
                                            options={itemTemp.produto?.atributos || []}
                                            getOptionLabel={(option) => `${option.st_nomeAtributo} (${option.st_tipo === 'MONETARIO' ? 'R$ ' : ''}${option.st_valorAtributo})`}
                                            value={itemTemp.atributoSelecionado}
                                            onChange={(_, newValue) => setItemTemp(prev => ({ ...prev, atributoSelecionado: newValue }))}
                                            disabled={!itemTemp.produto || !itemTemp.produto.atributos || itemTemp.produto.atributos.length === 0}
                                            fullWidth
                                            renderInput={(params) => (
                                                <TextField 
                                                    {...params} 
                                                    label="Atributo" 
                                                    size="small" 
                                                    placeholder={(!itemTemp.produto?.atributos || itemTemp.produto.atributos.length === 0) ? "Sem atributos" : "Selecione..."}
                                                    sx={{ bgcolor: 'background.paper' }} 
                                                    fullWidth 
                                                />
                                            )}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '0 1 100px' }}>
                                        <TextField
                                            label="Qtd. Atrib."
                                            size="small"
                                            value={itemTemp.nu_qtdAtributo || ''}
                                            disabled
                                            fullWidth
                                            sx={{ bgcolor: 'background.paper' }}
                                        />
                                    </Box>

                                    <Box sx={{ flex: '0 1 100px' }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={handleAddItem}
                                            disabled={!itemTemp.produto}
                                            startIcon={<AddIcon />}
                                            sx={{ height: 40 }}
                                        >
                                            Incluir
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Mensagens de Alerta */}
                                {itemTemp.produto && infoCalculoItem && infoCalculoItem.msgs.length > 0 && (
                                    <Box sx={{ mt: 2, width: '100%' }}>
                                        {infoCalculoItem.msgs.map((msg, idx) => (
                                            <Alert
                                                severity={msg.tipo}
                                                variant="outlined"
                                                icon={msg.tipo === 'warning' ? <WarningIcon fontSize="inherit" /> : <CalculateIcon fontSize="inherit" />}
                                                key={idx}
                                                size="small"
                                                sx={{ mt: 1, bgcolor: 'background.paper' }}
                                                action={
                                                    msg.tipo === 'warning' && infoCalculoItem.qtdSugerida > parseFloat(itemTemp.nu_quantidade) ? (
                                                        <Button
                                                            color="inherit"
                                                            size="small"
                                                            onClick={() => {
                                                                setItemTemp(prev => ({
                                                                    ...prev,
                                                                    nu_quantidade: infoCalculoItem.qtdSugerida
                                                                }));
                                                            }}
                                                        >
                                                            Aceitar Sugestão ({infoCalculoItem.qtdSugerida})
                                                        </Button>
                                                    ) : null
                                                }
                                            >
                                                {msg.texto}
                                                {infoCalculoItem.sobra && parseFloat(infoCalculoItem.sobra) > 0 && <strong> • Sobra: {infoCalculoItem.sobra}</strong>}
                                            </Alert>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* Tabela de Itens */}
                            <TableContainer sx={{ maxHeight: '30vh' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead component="thead">
                                        <TableRow sx={{ '& th': { bgcolor: theme.palette.mode === 'dark' ? '#333' : '#f0f0f0', fontWeight: 'bold', fontSize: '0.75rem' } }}>
                                            <TableCell width="18%">Produto</TableCell>
                                            <TableCell width="7%">Comp.(m)</TableCell>
                                            <TableCell width="5%">Unid.</TableCell>
                                            <TableCell width="6%">Qtd.</TableCell>
                                            <TableCell width="9%" align="right">Preço Unit.</TableCell>
                                            <TableCell width="6%" align="right">Desc %</TableCell>
                                            <TableCell width="10%">Atributo</TableCell>
                                            <TableCell width="8%" align="right">Qtd. Atrib.</TableCell>
                                            <TableCell width="10%" align="right">Subtotal</TableCell>
                                            <TableCell width="15%">Análise de Estoque</TableCell>
                                            <TableCell width="6%" align="right"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.itens.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                                        <ShoppingCartIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                                                        <Typography>Nenhum item adicionado ao orçamento.</Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            formData.itens.map((item, idx) => {
                                                const preco = parseFloat(item.vl_preco) || 0;
                                                const qtd = parseFloat(item.nu_quantidade) || 0;
                                                const desc = parseFloat(item.nu_desconto) || 0;
                                                const subtotal = (preco * qtd) * (1 - (desc / 100));

                                                const isComponent = item.isKitComponent;
                                                const isHeader = item.isKitHeader;

                                                return (
                                                    <TableRow
                                                        key={idx}
                                                        sx={{
                                                            bgcolor: isHeader ? (theme.palette.mode === 'dark' ? '#333' : '#f9f9f9') : 'inherit',
                                                            '& td': {
                                                                color: isComponent ? 'text.secondary' : 'text.primary',
                                                                borderBottom: isHeader ? 'none' : undefined,
                                                                fontSize: '0.75rem'
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ pl: isComponent ? 4 : 2, fontWeight: isHeader ? 'bold' : 'normal' }}>
                                                            {item.produto.st_nomeProduto}
                                                        </TableCell>
                                                        <TableCell>{(!isHeader && item.nu_comprimento_req) ? `${item.nu_comprimento_req}m` : '-'}</TableCell>
                                                        <TableCell>{item.unidadeSelecionada?.st_unidade || '-'}</TableCell>
                                                        <TableCell>{qtd}</TableCell>
                                                        <TableCell align="right">
                                                            {isComponent ? '-' : preco.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {(!isComponent && desc > 0) ? `${desc.toLocaleString('pt-BR')}%` : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.atributoSelecionado ? item.atributoSelecionado.st_nomeAtributo : '-'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {item.atributoSelecionado && item.nu_qtdAtributo ? parseFloat(item.nu_qtdAtributo).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                            {isComponent ? '-' : subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.calculo ? (
                                                                <Chip
                                                                    label={item.calculo.msgs.length > 0 ? item.calculo.msgs[0].texto : `Consumo: ${item.calculo.qtdPecasNecessarias} un`}
                                                                    color={item.calculo.msgs.some(m => m.tipo === 'warning') ? 'warning' : 'default'}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.65rem' }}
                                                                    title={item.calculo.msgs.map(m => m.texto).join('\n')}
                                                                />
                                                            ) : (
                                                                !isComponent && <Chip label="Padrão" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {!isComponent && (
                                                                <IconButton size="small" onClick={() => handleRemoverItem(idx)} color="error" title="Remover item">
                                                                    <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* SEÇÃO 3: RODAPÉ (BOX FLEX) */}
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap-reverse' }}>
                            <Box sx={{ flex: '1 1 500px' }}>
                                <TextField
                                    fullWidth
                                    label="Observações e Condições de Pagamento"
                                    value={formData.st_observacao}
                                    onChange={(e) => setFormData(prev => ({ ...prev, st_observacao: e.target.value }))}
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    placeholder="Digite aqui observações gerais, prazos de entrega e condições..."
                                    sx={{ bgcolor: 'background.paper' }}
                                />
                                {/* Sumário de Atributos */}
                                {formData.itens.some(i => i.atributoSelecionado) && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Totais por Atributo:</Typography>
                                        <Grid container spacing={2}>
                                                {Object.entries(formData.itens.reduce((acc, item) => {
                                                    if (item.atributoSelecionado && item.nu_qtdAtributo) {
                                                        const key = item.atributoSelecionado.st_nomeAtributo;
                                                        if (!acc[key]) acc[key] = { total: 0, tipo: item.atributoSelecionado.st_tipo };
                                                        acc[key].total += parseFloat(item.nu_qtdAtributo);
                                                    }
                                                    return acc;
                                                }, {})).map(([attrName, data]) => (
                                                    <Grid item xs={6} sm={4} key={attrName}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #ccc', pb: 0.5 }}>
                                                            <Typography variant="body2">{attrName}</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {data.tipo === 'MONETARIO' 
                                                                    ? data.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                                    : data.total.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                        </Grid>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ flex: '0 1 300px', minWidth: '250px' }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        height: '100%',
                                        bgcolor: theme.palette.mode === 'dark' ? '#424242' : '#eeeeee', // Cinza claro
                                        color: 'text.primary',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'flex-end',
                                        minHeight: '130px'
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>TOTAL ESTIMADO</Typography>
                                    <Typography variant="h3" fontWeight="bold">
                                        {formData.itens.reduce((acc, item) => {
                                            const preco = parseFloat(item.vl_preco) || 0;
                                            const qtd = parseFloat(item.nu_quantidade) || 0;
                                            const desc = parseFloat(item.nu_desconto) || 0;
                                            return acc + ((preco * qtd) * (1 - (desc / 100)));
                                        }, 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, fontSize: '10px' }}>
                                        * Valores sujeitos a alteração de estoque
                                    </Typography>
                                </Paper>
                            </Box>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={sharedStyles.dialogActions}>
                        <Button
                            startIcon={<PrintIcon />}
                            onClick={handleImprimir}
                            sx={{ mr: 'auto', color: 'text.secondary' }}
                        >
                            Imprimir
                        </Button>
                        <Button onClick={() => setOpenForm(false)} sx={sharedStyles.outlinedButton}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSalvar}
                            sx={sharedStyles.containedButton}
                        >
                            Salvar Orçamento
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </AcessoRestrito>
    );
}
